import {
    AppConfig,
    Application as AstroApp,
    Device,
    DeviceConfigPlugin,
    PreloadPlugin,
    ResizeFunction,
    ResizePlugin,
    ResizeReturn,
    ResourcePlugin,
    ScreensPlugin,
    SoundPlugin,
    StagePlugin,
    StatsPlugin,
    VisibilityPlugin,
} from '@goodboydigital/astro';
import { XY } from '@goodboydigital/odie';
import SoundBoy from '@goodboydigital/soundboy';
import { InteractionManager } from '@pixi/interaction';
import { Ticker, utils } from 'pixi.js';
import gsap from 'gsap';

import { Settings } from './AppConfig';
import { getManifest } from './AssetsManifest';
import { CustomDeviceSettings } from './DeviceSettings';
import { Game } from './game/Game';
import { AdsPlugin } from './plugins/AdsPlugin';
import { AnalyticsEvent, AnalyticsPlugin, AnalyticsState } from './plugins/AnalyticsPlugin';
import { APIPlugin } from './plugins/APIPlugin';
import { AssetsPlugin } from './plugins/AssetsPlugin';
import { ControllerPlugin } from './plugins/ControllerPlugin';
import { DataPlugin } from './plugins/DataPlugin';
import { LanguagePlugin } from './plugins/LanguagePlugin';
import { LocationPlugin } from './plugins/LocationPlugin';
import { NotchPlugin } from './plugins/NotchPlugin';
import { PopupPlugin } from './plugins/PopupPlugin';
import { RankPlugin } from './plugins/RankPlugin';
import { RewardPlugin } from './plugins/RewardPlugin';
import { SettingsPlugin } from './plugins/SettingsPlugin';
import { StoragePlugin } from './plugins/StoragePlugin';
import { UserPlugin } from './plugins/UserPlugin';
import { ChangeNameScreen } from './screens/ChangeNameScreen';
import { CharacterSelectScreen } from './screens/CharacterSelectScreen';
import { ControlScreen } from './screens/ControlScreen';
import { FlagSelectScreen } from './screens/FlagSelectScreen';
import { GameScreen } from './screens/GameScreen';
import { GarageScreen } from './screens/GarageScreen';
import { LanguageSelectScreen } from './screens/LanguageSelectScreen';
import { LoadingScreen } from './screens/LoadingScreen';
import { PreRaceScreen } from './screens/PreRaceScreen';
import { RankScreen } from './screens/RankScreen';
import { ResultsScreen } from './screens/ResultsScreen';
import { RideStudioBodyShopScreen } from './screens/RideStudioBodyShopScreen';
import { RideStudioExtrasShopScreen } from './screens/RideStudioExtrasShopScreen';
import { RideStudioScreen } from './screens/RideStudioScreen';
import { RideStudioTrailShopScreen } from './screens/RideStudioTrailShopScreen';
import { RideStudioWheelShopScreen } from './screens/RideStudioWheelShopScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { TitleScreen } from './screens/TitleScreen';
import { TermsScreen } from './screens/TermsScreen';
import { HomeScreen } from './screens/HomeScreen';
import { TutorialCompleteScreen } from './screens/TutorialCompleteScreen';
import { MenuClouds } from './ui/MenuClouds';
import { Browser } from './utils/Browser';
import { Maths } from './utils/Maths';

// Items that should be accessible from anywhere in the app
export const Shared = {
    assets: undefined as unknown as AssetsPlugin,
    data: undefined as unknown as DataPlugin,
    game: undefined as unknown as Game,
    ads: undefined as unknown as AdsPlugin,
    user: undefined as unknown as UserPlugin,
    rank: undefined as unknown as RankPlugin,
    settings: undefined as unknown as SettingsPlugin,
    location: undefined as unknown as LocationPlugin,
    controls: undefined as unknown as ControlScreen,
    controller: undefined as unknown as ControllerPlugin,
};

const APP_TARGET_WIDTH = 1024;
const APP_TARGET_HEIGHT = 768;

const manifest = getManifest();

export class App extends AstroApp
{
    readonly width = 1664;
    readonly height = 768;

    public stage!: StagePlugin;
    public resources!: ResourcePlugin;
    public resize!: ResizePlugin;
    public visibility!: VisibilityPlugin;
    public screens!: ScreensPlugin;
    public preloader!: PreloadPlugin;
    public controller!: ControllerPlugin;
    public assets!: AssetsPlugin;
    public storage!: StoragePlugin;
    public location!: LocationPlugin;
    public api!: APIPlugin;
    public user!: UserPlugin;
    public rank!: RankPlugin;
    public soundSetup!: SoundPlugin;
    public ads!: AdsPlugin;
    public popup!: PopupPlugin;
    public notch!: NotchPlugin;
    public reward!: RewardPlugin;
    public settings!: SettingsPlugin;
    public analytics!: AnalyticsPlugin;
    public data!: DataPlugin;
    public language!: LanguagePlugin;
    
    public clouds?: MenuClouds;
    
    private beforeUnload!: EventListener;
    private isReloading: boolean = false;

    constructor(config: AppConfig)
    {
        super(config);

        //Load analytics
        const script = document.createElement('script');
        script.src = _ANALYTICS_URL_;
        document.head.appendChild(script);
    }

    public async run(): Promise<void>
    {
        // Check if webgl is available before anything else
        if (!utils.isWebGLSupported())
        {
            this.tearDown('This environment does not support WebGL.');

            return;
        }

        this.add(DeviceConfigPlugin, {
            overrideConfig: CustomDeviceSettings,
        });
        // Default plugins
        this.resources = this.add(ResourcePlugin, {
            name: 'resource',
            manifest,
            version: Settings.quality,
        } as any);
        this.stage = this.add(StagePlugin, { name: 'stage' });
        this.visibility = this.add(VisibilityPlugin);
        this.resize = this.add(ResizePlugin, { resizeFunction: this.gameResize(APP_TARGET_WIDTH, APP_TARGET_HEIGHT) });
        this.screens = this.add(ScreensPlugin as any, { name: 'screens' }) as ScreensPlugin;
        this.preloader = this.add(PreloadPlugin, {
            name: 'preload',
            loaderScreenClass: LoadingScreen as any,
        });

        // Custom plugins
        this.language = this.add(LanguagePlugin);
        this.notch = this.add(NotchPlugin);
        this.controller = this.add(ControllerPlugin);
        this.assets = this.add(AssetsPlugin);
        this.data = this.add(DataPlugin);
        this.storage = this.add(StoragePlugin);
        this.location = this.add(LocationPlugin);
        this.api = this.add(APIPlugin);
        this.user = this.add(UserPlugin);
        this.rank = this.add(RankPlugin);
        this.popup = this.add(PopupPlugin);
        this.reward = this.add(RewardPlugin);
        this.settings = this.add(SettingsPlugin);
        this.analytics = this.add(AnalyticsPlugin);
        this.soundSetup = this.add(SoundPlugin, {
            manifest: manifest as any,
            manifestIDs: ['default'],
            registerSounds: () => this._registerSounds(),
        });
        this.ads = this.add(AdsPlugin);

        // Define shared plugins
        Shared.assets = this.assets;
        Shared.data = this.data;
        Shared.ads = this.ads;
        Shared.user = this.user;
        Shared.rank = this.rank;
        Shared.settings = this.settings;
        Shared.location = this.location;
        Shared.controller = this.controller;

        // FPS counter if in debug mode
        if (Settings.debug) this.add(StatsPlugin, { name: 'stats' });

        // Add screens then init the app
        this._addScreens();
        
        const preInitTime = new Date().getTime();
        
        await this.init();
        
        const postInitTime = new Date().getTime();
        const initDurationSecs = ((postInitTime - preInitTime) * 0.001).toFixed(2);
        
        // Init the analytics
        this.analytics.init();
        this.analytics.trackEvent(AnalyticsEvent.GAME_LOADDURATION, { ...AnalyticsState.HOMEPAGE.persistentMetaData, ...{ gameloadduration: initDurationSecs} })

        // Quick patch for ios portrait resize
        window.addEventListener('resize', () => this._enforceSize());
        window.addEventListener('orientationchange', () => this._enforceSize());
        
        this.beforeUnload = this._beforeUnload.bind(this);
        window.addEventListener("beforeunload", this.beforeUnload);

        window.addEventListener("blur", () => this._onWindowBlur());
        window.addEventListener("focus", () => this._onWindowFocus());
        document.addEventListener("visibilitychange", () => this._onVisibilityChange());

        //Prevent scrolling window with keyboard
        window.addEventListener("keydown", function(e) {
            // space and arrow keys
            if(e.key == "ArrowDown" || e.key == "ArrowUp" || e.key == "ArrowLeft" || e.key == "ArrowRight" || e.key == " "){
                e.preventDefault();
            }
            else if([32, 37, 38, 39, 40].indexOf(e.keyCode) > -1) {
                e.preventDefault();
            }
        }, false);

        this.ads.run();
    }
    
    /**
     * Trigger the resize method that propagate to all screens
     * The timeout is a brute force fix for ios safari, where the window dimensions are not
     * fully set after device rotation
     */
    private _enforceSize(): void
    {
        for (let i = 0; i < 20; i++)
        {
            setTimeout(() =>
            {
                this.resize.resize(window.innerWidth, window.innerHeight, true);
            }, 300 * i);
        }
    }

    /** Add all screens & overlays in the game */
    private _addScreens(): void
    {
        this.screens.add(GameScreen, { game: Game }, 'game');
        this.screens.overlay.add(TitleScreen, {}, 'splash');
        this.screens.overlay.add(HomeScreen, {}, 'home');
        this.screens.overlay.add(PreRaceScreen, {}, 'preRace');
        this.screens.overlay.add(GarageScreen, {}, 'garage');
        this.screens.overlay.add(CharacterSelectScreen, {}, 'characterSelect');
        this.screens.overlay.add(RideStudioScreen, {}, 'rideStudio');
        this.screens.overlay.add(SettingsScreen, {}, 'settings');
        this.screens.overlay.add(ChangeNameScreen, {}, 'changeName');
        this.screens.overlay.add(FlagSelectScreen, {}, 'flagSelect');
        this.screens.overlay.add(LanguageSelectScreen, {}, 'languageSelect');
        this.screens.overlay.add(TermsScreen, {}, 'terms');
        this.screens.overlay.add(RideStudioBodyShopScreen, {
            type: 'body',
        }, 'bodyStore');
        this.screens.overlay.add(RideStudioWheelShopScreen, {
            type: 'wheel',
        }, 'wheelStore');
        this.screens.overlay.add(RideStudioTrailShopScreen, {
            type: 'trail',
        }, 'trailStore');
        this.screens.overlay.add(RideStudioExtrasShopScreen, {
            type: 'extras',
        }, 'extrasStore');
        this.screens.overlay.add(ControlScreen, {}, 'controls');
        this.screens.overlay.add(RankScreen, {}, 'rank');
        this.screens.overlay.add(ResultsScreen, {}, 'results');
        this.screens.overlay.add(TutorialCompleteScreen, {}, 'tutorialComplete');
    }

    /** Register sound assets to Soundboy */
    private _registerSounds(): void
    {
        // register sounds from manifest:
        Object.values(manifest.default.audio).forEach((value: any) =>
        {
            // remove file extension from shortcut used as id
            const id = value.shortcut.split('.').shift();

            if (!id)
            {
                console.error(`[Soundboy] - Registration error no invalid id for ${value.shortcut}`);

                return;
            }
            const isMusic = id.indexOf('music') !== -1;
            const loop = isMusic || id.indexOf('loop') !== -1;
            const preload = true;
            const stream = false;

            SoundBoy.registerSound({
                id,
                src: [
                    value.mp3,
                    value.ogg,
                ],
                stream,
                loop,
                preload,
            });
        });
    }

    // stage position helpers
    get stageCenter(): XY
    {
        return { x: this.width * 0.5, y: this.height * 0.5 };
    }

    get stageLeft(): number
    {
        return ((this.width - this.resize.width) * 0.5) + this.notch.left;
    }

    get stageRight(): number
    {
        return ((this.width + this.resize.width) * 0.5) - this.notch.right;
    }

    get stageTop(): number
    {
        return ((this.height - this.resize.height) * 0.5) + this.notch.top;
    }

    get stageBottom(): number
    {
        return ((this.height + this.resize.height) * 0.5) - this.notch.bottom;
    }

    /** Window resize logic */
    public gameResize(
        logicalWidth: number,
        logicalHeight: number,
    ): ResizeFunction
    {
        return (w: number, h: number): ResizeReturn =>
        {
            const element = document.documentElement;

            // CSS is used to artificially 'rotate' the root element if window height is higher thn width
            element.style.position = 'fixed';
            element.style.overflow = 'hidden';
            element.style.pointerEvents = 'auto';
            element.style.userSelect = 'auto';

            // Swap w by h if height is higher than width
            if (h > w)
            {
                const tw = w;
                const th = h;

                w = th;
                h = tw;

                // PORTRAIT
                element.style.transformOrigin = 'left top';
                element.style.transform = 'rotate(-90deg)';
                element.style.width = '100vh';
                element.style.height = '100vw';
                element.style.left = '0vw';
                element.style.bottom = '-100%';

                Browser.exitFullScreen();
            }
            else
            {
                // LANDSCAPE
                element.style.transformOrigin = 'left top';
                element.style.transform = 'rotate(0deg)';
                element.style.width = '100%';
                element.style.height = '100%';
                element.style.left = 'auto';
                element.style.bottom = 'auto';
            }

            window.scrollTo(0, 0);

            const ratioW = w / logicalWidth;
            const ratioH = logicalHeight / h;

            return {
                rendererWidth: Math.round(Math.floor((logicalWidth * ratioW) * ratioH)),
                rendererHeight: logicalHeight,
                canvasWidth: w,
                canvasHeight: h,
            };
        };
    }
    
    public createMenuClouds()
    {
        if(!this.clouds){
            this.clouds = new MenuClouds(this);
            this.stage.stage.addChild(this.clouds.view);
            //this.clouds.view.visible = false;
            this.stage.stage.addChild(this.stage.mainContainer);
            this.stage.stage.addChild(this.stage.overlayContainer);
        }
        
    }
    
    public async hideMenuClouds(): Promise<void>
    {   
        if(this.clouds){            
            if (this.clouds.view.parent) this.clouds.view.parent.removeChild(this.clouds.view);
            this.clouds.view.visible = false;
            this.clouds = undefined;
        }

    }

    /**
     * Shut down the app, if something goes wrong, like not having webgl available
     */
    public tearDown(msg: string): void
    {
        Ticker.system.destroy();
        if (this.stage) this.stage.view.remove();

        console.log('[App] TEAR DOWN:', msg);

        const el = document.getElementById('message') || document.createElement('div');

        el.id = 'message';
        el.innerHTML = `
         <div id="message">
         <h1>${msg}</h1>
         </div>
         `;

        if (!el.parentNode) document.body.appendChild(el);
    }
    
    public reload(){
        this.isReloading = true;
        window.removeEventListener("beforeunload", this.beforeUnload);
        location.reload();
    }
    
    private _beforeUnload(){
        if(!this.isReloading){
            this.analytics.trackEvent(AnalyticsEvent.GAME_EXIT);
        }
        
    }
    
    private _onWindowBlur(){
        
    }

    private _onWindowFocus(){
        
    }
    
    private _onVisibilityChange() {
        if(document.visibilityState == 'hidden'){
            if(Settings.isApp() || Device.mobile){
                if(!this.isReloading){
                    this.analytics.trackEvent(AnalyticsEvent.GAME_EXIT);
                }
                
            }
            this._onWindowBlur();
        }
        else{
            this._onWindowFocus();
        }

    };
    
}

/**
 * Patch InteractionManager to transform interaction point from protrait to landscape
 * only if the device is on portrait - if window HEIGHT higher than WIDTH
 */
// eslint-disable-next-line func-names
InteractionManager.prototype.mapPositionToPoint = function (point: XY, x: number, y: number)
{
    point.x = x;
    point.y = y;

    if (window.innerWidth < window.innerHeight)
    {
        // Rotate
        Maths.rotatePoint(point, Maths.PI_HALF);

        // Translate
        point.x += window.innerHeight;

        // Scale
        const scale = APP_TARGET_HEIGHT / window.innerWidth;

        point.x *= scale;
        point.y *= scale;
    }
    else
    {
        // Scale
        const scale = APP_TARGET_HEIGHT / window.innerHeight;

        point.x *= scale;
        point.y *= scale;
    }
};
